import { useCallback, useMemo } from 'react';
import { CollectionView } from 'common';

interface UseCollectionFiltersArgs {
  collections: CollectionView[];
  viewerId: string;
  isAdminUser: boolean;
  activeTab: 'collections' | 'admin';
  filter: string;
}

export function useCollectionFilters({
  collections,
  viewerId,
  isAdminUser,
  activeTab,
  filter,
}: UseCollectionFiltersArgs) {
  const hasDirectAccess = useCallback(
    (collection: CollectionView) => {
      if (collection.mine) {
        return true;
      }
      if (!viewerId) {
        return true;
      }
      if (!collection.permissions || collection.permissions.length === 0) {
        return false;
      }
      return collection.permissions.some((permission) => {
        const permUser = permission.user as { _id?: string } | string;
        const permUserId = typeof permUser === 'string' ? permUser : permUser?._id;
        return permUserId === viewerId;
      });
    },
    [viewerId]
  );

  const personalCollections = useMemo(
    () => collections.filter((collection) => hasDirectAccess(collection)),
    [collections, hasDirectAccess]
  );

  const adminCollections = useMemo(
    () => (isAdminUser ? collections.filter((collection) => !hasDirectAccess(collection)) : []),
    [collections, hasDirectAccess, isAdminUser]
  );

  const displayedCollections = activeTab === 'admin' ? adminCollections : personalCollections;

  const filteredCollections = useMemo(() => {
    if (!filter) {
      return displayedCollections;
    }
    const normalizedFilter = filter.toLowerCase();
    return displayedCollections.filter((collection) =>
      collection.name.toLowerCase().includes(normalizedFilter)
    );
  }, [displayedCollections, filter]);

  return { personalCollections, adminCollections, displayedCollections, filteredCollections };
}
